import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Menu } from '../Menu';
import { SquareButton } from '../../../components/actions/SquareButton';
import { SquareButtonIconsEnum } from '../../../constants/square-button-icons';
import classes from './MenuDrawer.module.scss';
import { getNumberOfWorkspaces } from '../helpers/Workspace';
import { useUserData } from '../../../hooks/useUserData';
import { WorkspaceSwitcher } from '../TopNav.controller';
import { Select } from '../../../components/forms/Select';
import { BreakpointsValuesEnum } from '../../../constants/breakpoints-values';
import { idGenerator } from '../../../helpers/idGenerator';
import { useCallbackPrompt } from '../../../hooks/useCallbackPrompt';
import { useBreakpoints } from '../../../hooks/useBreakpoints';
import { useDidMount } from '../../../hooks/useDidMount';
import { ModalsContext } from '../../../providers/ModalsProvider';

export type MenuDrawerViewProps = {
  setOpen: (param: boolean) => void;
  workspaceSwitcher: WorkspaceSwitcher;
};

export const MenuDrawerView = (props: MenuDrawerViewProps): JSX.Element => {
  const [modalsRef, modalsUpdater] = useContext(ModalsContext);
  const [closeConfirmed, setCloseConfirmed] = useState<boolean>(false);
  const userData = useUserData();
  const showWorkspaceDropdown = getNumberOfWorkspaces(userData.userData?.organizations) > 1;
  const breakPoints: Array<keyof typeof BreakpointsValuesEnum> = useMemo(
    () => (showWorkspaceDropdown ? ['small', 'medium', 'tabletportrait'] : ['small', 'medium']),
    [showWorkspaceDropdown]
  );
  const breakpoint = useBreakpoints();
  const modalId = useMemo(() => idGenerator('modal'), []);
  const onClose = () => {
    props.setOpen && props.setOpen(false);
  };
  const onCloseAttempt = () => {
    const lastModalRef = modalsRef.current[modalsRef.current.length - 1];
    if (lastModalRef.id === modalId) {
      onClose();
    }
  };
  const { showPrompt, confirmNavigation, cancelNavigation } = useCallbackPrompt(modalId, onCloseAttempt);
  const navigationRef = useRef({ confirmNavigation, cancelNavigation });
  const drawerRef = useRef<HTMLDivElement>(null);

  useDidMount(() => {
    if (modalId && !modalsRef.current.filter(item => item.id === modalId).length) {
      modalsUpdater({ type: 'add', value: modalId })(modalsRef);
    }
    return () => {
      modalsUpdater({ type: 'remove', value: modalId })(modalsRef);
    };
  });

  useEffect(() => {
    const lastModalRef = modalsRef.current[modalsRef.current.length - 1];
    if (showPrompt && lastModalRef.id === modalId) {
      if (breakPoints.includes(breakpoint)) {
        if (closeConfirmed) {
          navigationRef.current.confirmNavigation();
        } else {
          navigationRef.current.cancelNavigation();
        }
      } else {
        navigationRef.current.confirmNavigation();
      }
    }
  }, [showPrompt, breakpoint, breakPoints, modalsRef, modalId, closeConfirmed]);

  return (
    <div className={classes.MenuDrawer} ref={drawerRef}>
      <div className={classes.CloseButtonContainer}>
        <SquareButton type={'white'} icon={SquareButtonIconsEnum.close} action={onClose} />
      </div>
      {showWorkspaceDropdown && (
        <div className={classes.WorkspaceDropdownButton}>
          <Select
            items={props.workspaceSwitcher.organizations}
            inlinedLabel
            name={'selectWorkspace'}
            useFormInstance={props.workspaceSwitcher.useFormInstance}
            rules={{}}
            size={'large'}
            type={'dark'}
            action={(value, complete) => {
              complete(true);
              props.workspaceSwitcher.changeWorkspaceAction(value);
            }}
            wrapper
          />
        </div>
      )}
      <div className={classes.MobileMenuButtons}>
        <Menu isMobileView closeMenu={() => setCloseConfirmed(true)} />
      </div>
    </div>
  );
};
